import type { TaxBreakdown as TaxBreakdownType } from '../../services/taxCalculations';

interface TaxBreakdownProps {
  breakdown: TaxBreakdownType;
  isPrimaCasa: boolean;
  venditoreImpresa: boolean;
  conMutuo: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}

export function TaxBreakdown({ breakdown, isPrimaCasa, venditoreImpresa, conMutuo }: TaxBreakdownProps) {
  return (
    <div className="space-y-6">
      {/* Riepilogo Veloce */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-lg font-medium mb-2">Totale Costi Accessori</h3>
          <span
            className={`text-xs px-2 py-1 rounded-full whitespace-nowrap ${
              breakdown.consuntivo ? 'bg-white/25' : 'bg-black/20'
            }`}
          >
            {breakdown.consuntivo ? 'importi dell’atto' : 'stima'}
          </span>
        </div>
        <p className="text-3xl font-bold">{formatCurrency(breakdown.totaleGenerale)}</p>
        <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-primary-200">Imposte</p>
            <p className="font-semibold">{formatCurrency(breakdown.totaleImposte)}</p>
          </div>
          <div>
            <p className="text-primary-200">Notaio</p>
            <p className="font-semibold">{formatCurrency(breakdown.totaleCostiNotarili)}</p>
          </div>
          <div>
            <p className="text-primary-200">Agenzia</p>
            <p className="font-semibold">{formatCurrency(breakdown.commissioneAgenzia)}</p>
          </div>
        </div>
      </div>

      {/* Dettaglio Imposte */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-xl">🏛️</span>
          Imposte di Acquisto
          <span className={`ml-auto text-xs px-2 py-1 rounded-full ${
            isPrimaCasa ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}>
            {isPrimaCasa ? 'Prima Casa' : 'Seconda Casa'}
          </span>
        </h3>

        <div className="space-y-3">
          {venditoreImpresa ? (
            <>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-700">IVA</p>
                  <p className="text-sm text-gray-500">{isPrimaCasa ? '4%' : '10%'} sul prezzo</p>
                </div>
                <p className="font-semibold text-gray-900">{formatCurrency(breakdown.iva)}</p>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <div>
                  <p className="font-medium text-gray-700">Imposta Registro</p>
                  <p className="text-sm text-gray-500">Fissa (acquisto da impresa)</p>
                </div>
                <p className="font-semibold text-gray-900">{formatCurrency(breakdown.impostaRegistro)}</p>
              </div>
            </>
          ) : (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Imposta Registro</p>
                <p className="text-sm text-gray-500">{isPrimaCasa ? '2%' : '9%'} sul valore catastale</p>
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(breakdown.impostaRegistro)}</p>
            </div>
          )}

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-700">Imposta Ipotecaria</p>
              <p className="text-sm text-gray-500">{venditoreImpresa ? 'Fissa' : 'Fissa'}</p>
            </div>
            <p className="font-semibold text-gray-900">{formatCurrency(breakdown.impostaIpotecaria)}</p>
          </div>

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-700">Imposta Catastale</p>
              <p className="text-sm text-gray-500">{venditoreImpresa ? 'Fissa' : 'Fissa'}</p>
            </div>
            <p className="font-semibold text-gray-900">{formatCurrency(breakdown.impostaCatastale)}</p>
          </div>

          {conMutuo && breakdown.impostaSostitutiva > 0 && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Imposta Sostitutiva Mutuo</p>
                <p className="text-sm text-gray-500">{isPrimaCasa ? '0.25%' : '2%'} sull'importo mutuo</p>
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(breakdown.impostaSostitutiva)}</p>
            </div>
          )}

          <div className="flex justify-between items-center py-3 bg-gray-50 rounded-lg px-3 mt-2">
            <p className="font-semibold text-gray-900">Totale Imposte</p>
            <p className="font-bold text-lg text-gray-900">{formatCurrency(breakdown.totaleImposte)}</p>
          </div>
        </div>
      </div>

      {/* Dettaglio Costi Notarili */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <span className="text-xl">📝</span>
          Costi Notarili
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-700">Onorario Atto Compravendita</p>
              <p className="text-sm text-gray-500">Rogito acquisto</p>
            </div>
            <p className="font-semibold text-gray-900">{formatCurrency(breakdown.onorarioCompravendita)}</p>
          </div>

          {conMutuo && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Onorario Atto Mutuo</p>
                <p className="text-sm text-gray-500">Iscrizione ipoteca</p>
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(breakdown.onorarioMutuo)}</p>
            </div>
          )}

          {/* A consuntivo le visure sono già dentro le spese accessorie dell'atto */}
          {!breakdown.consuntivo && (
            <div className="flex justify-between items-center py-2 border-b border-gray-100">
              <div>
                <p className="font-medium text-gray-700">Visure e Verifiche</p>
                <p className="text-sm text-gray-500">Catastali, ipotecarie, urbanistiche</p>
              </div>
              <p className="font-semibold text-gray-900">{formatCurrency(breakdown.visureVerifiche)}</p>
            </div>
          )}

          <div className="flex justify-between items-center py-2 border-b border-gray-100">
            <div>
              <p className="font-medium text-gray-700">
                {breakdown.consuntivo ? 'Spese accessorie' : 'Bolli e Diritti'}
              </p>
              <p className="text-sm text-gray-500">
                {breakdown.consuntivo
                  ? 'Cassa e CNN, tassa archivio, atto mutuo, visure'
                  : 'Spese accessorie'}
              </p>
            </div>
            <p className="font-semibold text-gray-900">{formatCurrency(breakdown.bolliDiritti)}</p>
          </div>

          <div className="flex justify-between items-center py-3 bg-gray-50 rounded-lg px-3 mt-2">
            <p className="font-semibold text-gray-900">Totale Notaio</p>
            <p className="font-bold text-lg text-gray-900">{formatCurrency(breakdown.totaleCostiNotarili)}</p>
          </div>
        </div>
      </div>

      {/* Commissione Agenzia */}
      {breakdown.commissioneAgenzia > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <span className="text-xl">🏠</span>
            Agenzia Immobiliare
          </h3>

          <div className="flex justify-between items-center py-3 bg-amber-50 rounded-lg px-3">
            <div>
              <p className="font-semibold text-gray-900">Commissione + IVA</p>
              <p className="text-sm text-gray-500">IVA 22% inclusa</p>
            </div>
            <p className="font-bold text-lg text-amber-700">{formatCurrency(breakdown.commissioneAgenzia)}</p>
          </div>
        </div>
      )}

      {/* Note informative */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 mb-2">ℹ️ Note</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          {!venditoreImpresa && (
            <li>• L'imposta di registro è calcolata sul valore catastale (se fornita la rendita)</li>
          )}
          {isPrimaCasa && (
            <li>• Agevolazioni prima casa applicate: aliquote ridotte e credito d'imposta</li>
          )}
          {conMutuo && (
            <li>• Imposta sostitutiva mutuo: {isPrimaCasa ? '0,25%' : '2%'} sull'importo finanziato</li>
          )}
          <li>• I costi notarili sono indicativi e possono variare</li>
        </ul>
      </div>
    </div>
  );
}
